<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('departure.Departure_UBN') }}</h5>
    <p class="mb-0 text-sm">{{ $t('departure.Note1Departure_to') }}</p>
    <p class="mb-0 text-sm">{{ $t('departure.Note2Departure_to') }}</p>
    <div class="multisteps-form__content">
      <div class="row mt-3">
        <div class="col-6">
            <div class="input-group input-group-static null">
                <label>{{ $t('departure.Departure_UBN') }}</label>
                <select id="departureUbnId" v-model="localFormData.departureUbnId" class="form-control form-control-default" :disabled="isDepartureUbnIdDisabled">
                    <option value="">{{ $t('departure.Select_UBN') }}</option>
                    <option v-for="item in departureubns" :key="item.id" :value="item.id">
                        {{ item.ubn }} - {{ item.locationFullAddressDropDown }} {{ item.shareName ? ' - ' + item.lastName : '' }}
                    </option>
                </select>
            </div>
        </div>
        <div class="col-6">
          <div class="input-group input-group-static null">
              <label class="">{{ $t('departure.Departure_UBN_outside_studboek') }}</label>              
              <input v-model="localFormData.departureUbn" @blur="onDepartureUbnBlur" :disabled="isDepartureUbnDisabled" type="text" class="form-control form-control-default" :placeholder="$t('departure.Departure_UBN_outside_studboek')">
            </div>
        </div>
      </div>
      <div class="row mt-3" v-if="userRoleName == 'Admin' || userRoleName == 'AssociationAdmin'">
        <div class="col-6">
            <div class="input-group input-group-static null">
                <label>{{ $t('departure.Export_country') }}</label>
                <select id="exportCountryId" v-model="localFormData.exportCountryId" class="form-control form-control-default" :disabled="isExportCountryDisabled">
                    <option value="">{{ $t('departure.Select_export_country') }}</option>
                    <option v-for="item in exportcountries" :key="item.id" :value="item.id">
                        [{{ item.name }}] {{ item.description }}
                    </option>
                </select>
            </div>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col-12">
          <div v-if="wasValidated">
            <!-- Departure UBN or outside UBN is required -->
            <span v-if="v$.formLevel?.atLeastOneProvided?.$invalid" class="text-sm text-danger d-block">
              {{ v$.formLevel?.atLeastOneProvided?.$message }}
            </span>

            <!-- Export country requires outside studboek UBN -->
            <span v-if="v$.formLevel?.exportCountryRequiresUbn?.$invalid" class="text-sm text-danger d-block">
              {{ v$.formLevel?.exportCountryRequiresUbn?.$message }}
            </span>
          </div>
        </div>
      </div>
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="validateAndPrevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>                
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, watch, ref } from 'vue'
  import { helpers } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'

  export default {
    name: "DepartureUbn",
    props: {
      departureubns: Array,
      exportcountries: Array,
      formData: Object,
      prevStep: Function,
      nextStep: Function
    },
    computed: {    
        userRoleName() {
            return this.$store.state.user?.roleName;
        }        
    },
    setup(props, { emit }) {
      const { t } = useI18n()
      const localFormData = reactive(props.formData);

      const wasValidated = ref(false);

      const onDepartureUbnBlur = () => {
        const value = localFormData.departureUbn?.trim()

        if (!value) {
          localFormData.departureUbn = ''
          emit('textbox-empty')
          return
        }

        // User entered an outside-studboek UBN, so the studboek UBN cannot remain selected.
        if (localFormData.departureUbnId) {
          localFormData.departureUbnId = ''
        }

        emit('textbox-has-text')
      }

      const isDepartureUbnIdSelected = computed(() => { return !!localFormData.departureUbnId })
      const isDepartureUbnTyped = computed(() => { return !! (localFormData.departureUbn && localFormData.departureUbn.trim())})
      const isDepartureUbnDisabled = computed(() => { return isDepartureUbnIdSelected.value })
      const isDepartureUbnIdDisabled = computed(() => { return isDepartureUbnTyped.value })
      const isExportCountryDisabled = computed(() => { return isDepartureUbnIdSelected.value })

      const rules = computed(() => ({
        departureUbnId: {
          // No individual rules; handled in cross-field
        },
        departureUbn: {
          // No individual rules; handled in cross-field
        },
        formLevel: {
          atLeastOneProvided: helpers.withMessage(
            t('departure.Either_studboek_UBN_or_UBN_outside_studboek_required'),
            () => {
              const hasDepartureUbnId = !!localFormData.departureUbnId

              const hasDepartureUbn = !!(
                localFormData.departureUbn &&
                localFormData.departureUbn.trim()
              )

              return hasDepartureUbnId || hasDepartureUbn
            }
          ),

          exportCountryRequiresUbn: helpers.withMessage(
            t('departure.Export_country_requires_an_outside_studboek_UBN'),
            () => {
              const hasExportCountry = !!localFormData.exportCountryId

              const hasDepartureUbn = !!(
                localFormData.departureUbn &&
                localFormData.departureUbn.trim()
              )

              // If export country is empty, no problem.
              // If export country has value, departureUbn is mandatory.
              return !hasExportCountry || hasDepartureUbn
            }
          )
        }
      }))
      
      const v$ = useVuelidate(rules, localFormData)
  
      watch(localFormData, () => {
        emit('update:formData', localFormData)
      }, { deep: true })

      const validateAndPrevStep = async () => {
        wasValidated.value = true
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          props.prevStep()
        }
      }

      const validateAndNextStep = async () => {  
        wasValidated.value = true    
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          props.nextStep()
        }
      }

      return {
        localFormData,
        v$,
        wasValidated,

        isDepartureUbnTyped,
        isDepartureUbnIdSelected,

        isDepartureUbnDisabled,
        isDepartureUbnIdDisabled,
        isExportCountryDisabled,

        onDepartureUbnBlur,
        validateAndPrevStep,
        validateAndNextStep        
      }
    }    
};
</script>
<style scoped>
.card {
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  position: relative;
  padding: 10px !important;
}
.cursor-pointer {
  cursor: pointer;
}
.bg-light {
  background-color: #f9f9f9 !important;
}
.border-success {
  border-color: #28a745 !important;
}
</style>